import {
  EllipsisIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UsersIcon,
} from "lucide-react";

import { Column, EmptyState, Text } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useUpdateSetStafferEmployeeReports } from "@/hooks/updateSets/useUpdateSetStafferEmployeeReports";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { userRoleBadgeClasses, userRoleToLabel } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { User } from "@/types";

export const UsersTable = ({
  users,
  onCreate,
  onEdit,
  onDelete,
}: {
  users: User[];
  onCreate: () => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateStafferEmployeeReports } =
    useUpdateSetStafferEmployeeReports();

  /* React Hooks */
  const successToast = useSuccessToast();

  /* Handlers */
  const handleToggleStafferEmployeeReports = (
    user: User,
    receivesStafferEmployeeReports: boolean,
  ) => {
    updateStafferEmployeeReports(
      { userId: user.id, receivesStafferEmployeeReports },
      {
        onSuccess: () => {
          successToast({
            description: receivesStafferEmployeeReports
              ? `${user.firstName} now receives staffer employee reports`
              : `${user.firstName} no longer receives staffer employee reports`,
          });
        },
      },
    );
  };

  /* Render */
  const renderActionsCell = (user: User) => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost">
            <EllipsisIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              onEdit(user);
            }}
          >
            <PencilIcon className="h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-red-600 focus:text-red-600"
            onClick={() => {
              onDelete(user);
            }}
          >
            <Trash2Icon className="h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <Column className="overflow-auto">
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="border-none">
              <TableHead className="w-[28%]">
                <Text variant="header">Name</Text>
              </TableHead>
              <TableHead className="w-[32%]">
                <Text variant="header">Email</Text>
              </TableHead>
              <TableHead className="w-[14%]">
                <Text variant="header">User Role</Text>
              </TableHead>
              <TableHead className="w-[14%]">
                <Text variant="header">Created</Text>
              </TableHead>
              <TableHead className="w-[16%]">
                <Text variant="header">Employee reports</Text>
              </TableHead>
              <TableHead className="w-[6%]" />
            </TableRow>
          </TableHeader>
        </Table>
      </div>
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table className="table-fixed">
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell className="p-0" colSpan={6}>
                  <EmptyState
                    className="bg-slate-50 py-14"
                    description="No users were found. Add a user to get started!"
                    handlePrimaryClick={onCreate}
                    Icon={UsersIcon}
                    PrimaryButtonIcon={PlusIcon}
                    primaryButtonName="Add user"
                    title="No users found"
                  />
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="w-[28%]">
                    <Text truncate>
                      {user.firstName} {user.lastName}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[32%]">
                    <Text truncate variant="secondary">
                      {user.email}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[14%]">
                    {user.userRole ? (
                      <Badge
                        className={userRoleBadgeClasses[user.userRole]}
                        variant="outline"
                      >
                        {userRoleToLabel[user.userRole]}
                      </Badge>
                    ) : (
                      <Text className="text-sm italic text-slate-400">
                        Unassigned
                      </Text>
                    )}
                  </TableCell>
                  <TableCell className="w-[14%]">
                    <Text truncate variant="secondary">
                      {dates.formatIsoDateTimeForUser(user.createdAt)}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[16%]">
                    <Switch
                      checked={
                        user.userSettings?.receivesStafferEmployeeReports ??
                        false
                      }
                      onCheckedChange={(checked) => {
                        handleToggleStafferEmployeeReports(user, checked);
                      }}
                    />
                  </TableCell>
                  <TableCell className="w-[6%]">
                    {renderActionsCell(user)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Column>
  );
};
